import React, { useEffect, useState } from 'react';
import { Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import {
  formatOralExamJoinWindowCloseTime,
  isOralExamJoinWindowBypassEnabled,
  isOralExamLecturerJoinWindowClosed,
  oralExamJoinWindowRemainingMinutes,
  resolveOralExamStudentJoinPhase,
  resolveOralExamWaitingTimePhase,
} from '@/common/utils/oralExam/studentJoinWindow';

export type JoinGraceLineProps = {
  bookingId: string;
  slotStartIso: string;
  slotEndIso: string;
  size?: 'xs' | 'sm' | 'md';
};

export function JoinGraceLine({
  bookingId, slotStartIso, slotEndIso, size = 'xs',
}: JoinGraceLineProps) {
  const { t } = useTranslation();
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const joinClosed = isOralExamLecturerJoinWindowClosed(slotStartIso, slotEndIso);
  if (joinClosed) {
    return (
      <Text
        size={size}
        color="$statusWarning"
        testID={`id-review-join-grace-${bookingId}`}
      >
        {t('oralExam.lecturer.idReview.joinClosed', {
          defaultValue: 'Join closed — cannot enter.',
        })}
      </Text>
    );
  }

  const joinPhase = resolveOralExamStudentJoinPhase(slotStartIso, slotEndIso, undefined, {
    bypassWindow: isOralExamJoinWindowBypassEnabled(),
  });
  if (joinPhase === 'too_early') return null;
  // During the grace period the page subtitle and start reason carry the deadline.
  if (resolveOralExamWaitingTimePhase(slotStartIso) === 'during_grace') return null;

  const closeTime = formatOralExamJoinWindowCloseTime(slotStartIso);
  const remaining = oralExamJoinWindowRemainingMinutes(slotStartIso);

  return (
    <Text
      size={size}
      color="$textSubtle"
      testID={`id-review-join-grace-${bookingId}`}
    >
      {t('oralExam.lecturer.idReview.joinGraceOpen', {
        defaultValue: 'Can join until {{closeTime}} · {{remaining}} min left',
        closeTime,
        remaining,
      })}
    </Text>
  );
}
